 import { AddObjectKeyRequest, UpdateObjectKeyRequest, UpdateObjectValueRequest } from "@/backend/express/controllers/fieldObjectController";
import { Parameter } from "@/backend/models/parameter";
import { Validation } from "@/backend/models/validation";
import { CreateCustomFieldReply, CustomFieldCreateParams, CustomFieldParams } from "@/types/customFieldTypes";
import { CreateReferenceRequest, DataTypes, ItemReferenceParams } from "@/types/itemReferenceTypes";
import { ParameterCreateRequest, ParameterDeleteRequest, ParameterParams, ParameterReply, ParameterSubjectType, ParameterType, ParameterUpdateReply, ParameterUpdateRequest, UpdatedParamsAndRefs } from "@/types/parameterTypes";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { FieldObjectController } from "../controllers/fieldObjectController";
import { CustomFieldController } from "../controllers/customFieldController";
import { ObjectCustomField } from "@/backend/models/customFields/objectCustomField";
import { ParameterController } from "../controllers/parameterController";
import { FusaReply } from "@/types/replyTypes";
import { CustomFieldBuilder } from "@/backend/access/customFields/customFieldBuilder";
import { ParameterGenerateValuesRequest, ParameterGenerateObjectValuesRequest, ParameterSelectFieldRequest } from "@/backend/express/controllers/parameterController";
import { REGEX } from "@/backend/lib/regex";
import { I18n } from "@/i18n/I18n";
import { ReferenceController } from "../controllers/referenceController";
import { CustomField } from "@/backend/models/customFields/customField";
import { UpdatedConsumers, useTreeStore } from "./TreeStore";
import { OPERAND_TYPE, ValidationParams } from "@/types/validationTypes";
import { ContextLogController } from "../controllers/contextLogController";
import { ContextLog } from "@/backend/models/contextLog";
import { CONTEXT_LOG_STATUS, CONTEXT_LOG_SUBJECT_TYPES, ContextLogCreateRequest } from "@/types/contextLogTypes";
import { ValidationController } from "../controllers/validationController";
import { ValidationUpdateRequest } from "@/backend/express/controllers/validationController";
import { useValidationStore } from "./ValidationStore";
import { nativeBridge } from "../embed/nativeBridge";
import { PendingValidation } from "@/types/validationTypes";

type NewParameterInfo = {
  subjectId: string;
  subjectType: ParameterSubjectType;
  identifier: string;
  type: ParameterType;
};

export enum ValidationStatus {
  RUNNING = 'running',
  PASSING = 'passing',
  OVERIDDEN = 'overidden',
  FAILING = 'failing',
  NONE = 'none',
}

type ParameterStore = {
  parameters: Parameter[];
  references: ItemReferenceParams[];
  validations: Validation[];
  contextLogs: ContextLog[];
  reset: () => void;
  setParameters: (parameters: ParameterParams[]) => void;
  createParameter: (request: ParameterCreateRequest) => Promise<ParameterReply>;
  deleteParameter: (request: ParameterDeleteRequest) => Promise<FusaReply>;
  updateParameter: (request: ParameterUpdateRequest) => Promise<ParameterUpdateReply>;
  fetchParameter: (id: string) => Promise<void>;
  fetchParameters: (ids: string[]) => Promise<void>;
  handleParameterResponse: (updatedParameters: ParameterParams[]) => void;
  parametersForSubjectAndType: (subjectId: string, type: ParameterType) => Parameter[];
  parametersForSubject: (subjectId: string, subjectType: ParameterSubjectType) => Parameter[];
  getParametersForSubject: (subjectId: string, subjectType: ParameterSubjectType) => Promise<void>;
  getParametersForSubjects: (subjectIds: string[], subjectType: ParameterSubjectType) => Promise<void>;
  createReference: (request: CreateReferenceRequest) => Promise<void>;
  deleteReference: (referenceId: string | undefined) => Promise<void>;
  refetchConsumers: (updatedReferences: ItemReferenceParams[]) => Promise<void>;
  enableReference: (referenceId: string) => Promise<void>;
  handleReferenceResponse: (updatedReferences: ItemReferenceParams[]) => void;
  parameterIdentifierInvalid: (parameter: Parameter | NewParameterInfo) => string | undefined;
  findFieldForOperation: (operationId: string) => CustomField | undefined;
  selectField: (request: ParameterSelectFieldRequest) => Promise<void>;
  handleUpdatedParamsAndRefs: (updatedConsumers: UpdatedParamsAndRefs) => void;
  addObjectKey: (request: AddObjectKeyRequest, updateConsumers: (response: UpdatedParamsAndRefs) => void) => Promise<void>;
  updateObjectKey: (request: UpdateObjectKeyRequest, updateConsumers: (response: UpdatedConsumers) => void) => Promise<void>;
  deleteObjectKey: (id: string, updateConsumers: (response: UpdatedParamsAndRefs) => void) => Promise<void>;
  updateObjectValue: (request: UpdateObjectValueRequest, updateConsumers: (response: UpdatedParamsAndRefs) => void) => Promise<void>;
  updateObjectInstance: (title: string, customField: ObjectCustomField) => Promise<void>;
  generateParameterValues: (request: ParameterGenerateValuesRequest) => Promise<void>;
  generateObjectParameterValues: (request: ParameterGenerateObjectValuesRequest) => Promise<void>;
  parametersForSubjectsAndTypes: (subjectIds: string[], types: ParameterType[]) => Parameter[];
  updateCustomField: (request: CustomFieldParams) => Promise<UpdatedParamsAndRefs>;
  runPendingValidations: (pendingValidations: PendingValidation[]) => Promise<void>;
  createCustomField: (request: CustomFieldCreateParams) => Promise<CreateCustomFieldReply>;
  deleteCustomField: (customField: CustomFieldParams) => Promise<FusaReply>;
  setReferences: (references: ItemReferenceParams[]) => void;
  setValidations: (validations: ValidationParams[]) => void;
  getValidationForSubject: (subjectId: string) => Validation[];
  validationsForParameters: (subjectIds: string[]) => Validation[];
  parameterValidationsPassing: (parameterIds: string[]) => boolean;
  updateValidation:(request: ValidationUpdateRequest) => Promise<void>;
  rerunValidation: (validationId: string) => Promise<void>;
  deleteValidation: (validationId: string) => Promise<void>;
  createValidationForParameter: (parameterId: string) => Promise<void>;
  fetchParametersForFunctionalInterface: (functionalInterfaceId: string) => Promise<void>;
  addContextLog: (request: ContextLogCreateRequest) => Promise<void>;
  createParameterContextLog: (parameterId: string) => Promise<void>;
  commitParameterContextLog: (contextLogId: string, note: string, userEmail: string) => Promise<void>;
  fetchContextLogs: (rootId: string) => Promise<void>;
  deleteContextLogAndResetParameter: (contextLog: ContextLog) => Promise<void>;
  deleteContextLogsForSubject: (subjectId: string, subjectType: CONTEXT_LOG_SUBJECT_TYPES) => Promise<void>;
  deleteContextLog: (contextLogId: string) => Promise<void>;
  fetchContextLogParametersForContextLogs: (contextLogIds: string[]) => Promise<void>;
  getValidationStatus: (validationId: string) => ValidationStatus;
  getValidationStatusesForSubject: (subjectId: string) => ValidationStatus[];
  getValidationStatusMapForSubject: (subjectId: string) =>  Map<string, ValidationStatus>;
  getParametersForImplementationFunction: (implementationFunctionId: string) => Promise<Parameter[]>;
  createContainerValidation: (containerId: string) => Promise<void>;
  handleValidationResponse: (updatedValidations: ValidationParams[]) => void;
  createProcedureTestParameters: (procedureId: string) => Promise<void>;
  creatingTestParameters: boolean;
};

const initialState = {
  parameters: [] as Parameter[],
  references: [] as ItemReferenceParams[],
  validations: [] as Validation[],
  contextLogs: [] as ContextLog[],
  creatingTestParameters: false,
};

export const useParameterStore = create<ParameterStore>()(immer((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setParameters: (parameters) => {
    set({ parameters: parameters.map((param) => new Parameter(param)) });
  },
  setReferences: (references: ItemReferenceParams[]) => {
    set({ references });
  },
  createParameter: async (request) => {
    const response = await new ParameterController().createParameter(request);
    if (!response.errors && response.parameter) {
      get().setParameters([...get().parameters, response.parameter]);
    }
    return response;
  },
  deleteParameter: async (request: ParameterDeleteRequest) => {
    const controller = new ParameterController();
    const { setParameters, parameters, references } = get();
    const response = await controller.deleteParameter(request.id);
    if (!response.errors) {
      setParameters(parameters.filter((param) => param.id !== request.id));
      set({ references: references.filter(ref =>
        !(ref.dataConsumerId === request.id && ref.dataConsumerType === DataTypes.Parameter)
        && !(ref.dataSourceId === request.id && ref.dataSourceType === DataTypes.Parameter)
      )});
    }
    return response;
  },
  updateParameter: async (request) => {
    const response = await new ParameterController().updateParameter(request);
    if (!response.errors) {
      useTreeStore.getState().handleUpdatedTreeConsumers(response);
      get().handleParameterResponse(response.updatedParameters);
    }
    return response;
  },
  fetchParameter: async (id: string) => {
    const response = await new ParameterController().readParameter(id);
    if (!response.errors && response.parameter) {
      get().handleParameterResponse([response.parameter]);
    }
  },
  fetchParameters: async (ids: string[]) => {
    const response = await new ParameterController().readByIds(ids);
    if (!response.errors && response.parameters) {
      get().handleParameterResponse(response.parameters);
    }
  },
  handleParameterResponse: (updatedParameters) => {
    if (updatedParameters.length === 0) return;
    const { parameters, setParameters } = get();
    const currentParamIds = parameters.map(param => param.id);
    const newParameters: ParameterParams[] = [];
    const updatedParameterLookup: { [key: string]: ParameterParams } = {};

    updatedParameters.forEach(param => {
      if (!currentParamIds.includes(param.id)) {
        newParameters.push(new Parameter(param));
      } else {
        updatedParameterLookup[param.id] = param;
      }
    });

    setParameters([...parameters.map(parameter => updatedParameterLookup[parameter.id] || parameter), ...newParameters]);
  },
  parametersForSubjectAndType: (subjectId: string, type: ParameterType) => {
    return get().parameters.filter((param) => param.subjectId === subjectId && param.type === type);
  },
  parametersForSubject: (subjectId: string, subjectType: ParameterSubjectType) => {
    return get().parameters.filter((param) => param.subjectId === subjectId && param.subjectType === subjectType);
  },
  getParametersForSubject: async (subjectId: string, subjectType: ParameterSubjectType) => {
    return new ParameterController().readParametersBySubject({ subjectId, subjectType }).then((response) => {
      if (response.errors || !response.parameters) return;
      get().handleParameterResponse(response.parameters);
    });
  },
  getParametersForSubjects: (subjectIds: string[], subjectType: ParameterSubjectType) => {
    return new ParameterController().readAllParametersBySubjects(subjectIds, subjectType).then((response) => {
      if (response.errors || !response.parameters) return;
      get().handleParameterResponse(response.parameters);
    });
  },
  getParametersForImplementationFunction: async (implementationFunctionId: string) => {
    return new ParameterController().readAllParametersForImplementationFunction(implementationFunctionId).then((response) => {
      if (response.errors || !response.parameters) return [];
      get().handleParameterResponse(response.parameters);
      return response.parameters.map((param) => new Parameter(param));
    });
  },
  createReference: async (request: CreateReferenceRequest) => {
    const response = await new ReferenceController().createReference(request);
    if (!response.errors && response.reference) {
      set({ references: [...get().references, response.reference] });
      get().handleParameterResponse(response.updatedParameters);
    }
  },
  deleteReference: async (referenceId?: string) => {
    if (!referenceId) return;
    const controller = new ReferenceController();
    const response = await controller.deleteReference(referenceId);
    if (!response.errors) {
      set({ references: get().references.filter((reference) => reference.id !== referenceId) });
    }
  },
  refetchConsumers: async (updatedReferences) => {
    updatedReferences.forEach((reference) => {
      if (reference.dataConsumerType === DataTypes.Parameter) {
        void get().fetchParameter(reference.dataConsumerId);
      }
    });
  },
  enableReference: async (referenceId) => {
    const response = await  new ReferenceController().enableReference(referenceId);
    if (!response.errors && response.reference) {
      const updatedReference = response.reference;
      const updatedReferences = get().references.map((reference) => reference.id === referenceId ? updatedReference : reference);
      set({ references: updatedReferences });
      void get().refetchConsumers([updatedReference]);
    }
  },
  handleReferenceResponse: (updatedReferences) => {
    const { references } = get();
    if (updatedReferences.length > 0) {
      const responseIds = updatedReferences.map(ref => ref.id);
      const filteredReferences = references.filter(ref => !responseIds.includes(ref.id));
      set({ references: [...filteredReferences, ...updatedReferences] });
    }
  },
  parameterIdentifierInvalid: (parameter) => {
    if (!parameter.identifier) return undefined;

    if (!REGEX.IDENTIFIER.test(parameter.identifier)) {
      return I18n.get('identifierAlphaNumeric');
    }

    const allParameters = get().parameters.filter((param) => (!('id' in parameter) || param.id !== parameter.id));

    const siblings = allParameters.filter((param) => param.subjectId === parameter.subjectId);

    if (siblings.some((param) => param.identifier === parameter.identifier && param.type === parameter.type)) {
      return I18n.get('uniqueIdentifierError', { subjectType: I18n.get(parameter.subjectType), type: I18n.get(parameter.type) });
    };

    const procedureParams = allParameters.filter((param) => param.subjectType === ParameterSubjectType.PROCEDURE && param.type !== ParameterType.INTRODUCTION);

    if (parameter.subjectType === ParameterSubjectType.STEP && procedureParams.some((param) => param.identifier === parameter.identifier)) {
      return I18n.get('stepInputIdentifierNotUnique');
    }

    const stepParams = allParameters.filter((param) => param.subjectType === ParameterSubjectType.STEP);
    if (parameter.subjectType === ParameterSubjectType.PROCEDURE) {
      if (stepParams.some((param) => param.identifier === parameter.identifier)) {
        return I18n.get('procedureParamIdentifierNotUnique');
      }
      if (procedureParams.some((param) => param.identifier === parameter.identifier)) {
        return I18n.get('procedureParamIdentifierNotUnique');
      }
    }

    return undefined;
  },
  findFieldForOperation: (operationId: string) => {
    const { parameters } = get();
    const parameter = parameters.find((p) => p.subjectId === operationId && p.subjectType === ParameterSubjectType.OPERATION && p.type === ParameterType.FIELD);
    return parameter?.fields[0];
  },
  selectField: async (request: ParameterSelectFieldRequest) => {
    const response = await new ParameterController().selectField(request);
    if (response.status === 200) {
      get().handleUpdatedParamsAndRefs(response);
    }
  },
  /**
   * Every cascade's answer arrives here -- from a store action, from a
   * component, from a run relayed over the bridge -- which makes it the one
   * place on this side that can do what desktop does from inside the cascade
   * itself (referenceService.ts:86): run whatever the write made stale.
   *
   * Wiring it here rather than at each caller is the point. The risk in
   * covering these paths one by one is missing one, and a missed path does not
   * fail: the validation keeps a verdict that is no longer true.
   */
  handleUpdatedParamsAndRefs: (updatedConsumers) => {
    const { updatedParameters, updatedReferences, pendingValidations } = updatedConsumers;
    get().handleParameterResponse(updatedParameters);
    get().handleReferenceResponse(updatedReferences);

    // Not awaited: the write is saved and applied. The spinner on each
    // validation is what waits. A validation's own result carries no pending
    // validations, so applying one cannot set this going again.
    if (pendingValidations?.length) void get().runPendingValidations(pendingValidations);
  },
  addObjectKey: async (request, handleUpdatedConsumers) => {
    const response = await new FieldObjectController().addObjectKey(request);
    if (response.status === 200) {
      handleUpdatedConsumers({ updatedReferences: [], ...response });
    }
  },
  updateObjectKey: async (request, handleUpdatedConsumers) => {
    const response = await new FieldObjectController().updateObjectKey(request);
    if (response.status === 200) {
      handleUpdatedConsumers(response);
    }
  },
  deleteObjectKey: async (id, handleUpdatedConsumers) => {
    const response = await new FieldObjectController().deleteObjectKey(id);
    if (response.status === 200) {
      set({ references: get().references.filter(ref => ref.sourceObjectKeyId !== id && ref.consumerObjectKeyId !== id) });
      handleUpdatedConsumers({ updatedReferences: [], ...response });
    }
  },
  updateObjectValue: async (request, handleUpdatedConsumers) => {
    const response = await new FieldObjectController().updateObjectValue(request);
    if (response.status === 200) {
      handleUpdatedConsumers({ ...response });
    }
  },
  updateObjectInstance: async (title, customField) => {
    const response = await new FieldObjectController().updateObjectInstance({ title, id: customField.objectInstance.id });
    if (response.status === 200) {
      void get().fetchParameter(customField.subjectId);
    }
  },
  generateParameterValues: async (request: ParameterGenerateValuesRequest) => {
    const response = await new ParameterController().generateParameterValues(request);
    if (response.parameter) {
      get().handleParameterResponse([response.parameter]);
    }
  },
  generateObjectParameterValues: async (request: ParameterGenerateObjectValuesRequest) => {
    const response = await new ParameterController().generateObjectParameterValues(request);
    if (response.parameter) {
      get().handleParameterResponse([response.parameter]);
    }
  },
  parametersForSubjectsAndTypes: (subjectIds: string[], types: ParameterType[]) => {
    return get().parameters.filter((p) => subjectIds.includes(p.subjectId) && types.includes(p.type));
  },
  createCustomField: async (req) => {
    const response = await new CustomFieldController().create(req);
    if (response.customField) {
      const customField = new CustomFieldBuilder().build(response.customField);
      void get().fetchParameter(customField.subjectId);
    }
    return response;
  },
  deleteCustomField: async (customField) => {
    const response = await new CustomFieldController().delete({ id: customField.id });
    if (response.status === 200) {
      const { parameters } = get();
      const parameter = parameters.find(p => p.id === customField.subjectId);
      if (parameter) {
        void get().fetchParameter(parameter.id);
      }
    }
    return response;
  },
  updateCustomField: async (customField) => {
    const response = await new CustomFieldController().update({ customField });
    if (!response.errors && response.status === 200) {
      get().handleUpdatedParamsAndRefs(response);
    }

    return response;
  },

  /**
   * Runs the validations an edit made stale, on the desktop app's runner.
   *
   * Desktop re-runs them inside the cascade itself; the runner is on the
   * user's machine, so the server says which are stale and this asks. One at a
   * time, as desktop runs them (validationRunService.ts:69), and each is
   * applied as it lands rather than in a batch at the end.
   *
   * In an ordinary browser there is nothing to ask, so they keep whatever
   * verdict they had -- the same as before any of this.
   */
  runPendingValidations: async (pendingValidations) => {
    if (pendingValidations.length === 0 || !nativeBridge.isAvailable()) return;

    useValidationStore.getState().startValidationRun(pendingValidations.map(v => v.validationId));

    for (const pending of pendingValidations) {
      try {
        const reply = await nativeBridge.call<{ result?: UpdatedParamsAndRefs }>('validation.run', pending);
        if (reply?.result) get().handleUpdatedParamsAndRefs(reply.result);
      } catch {
        // The runner could not be reached, or could not save. The validation
        // keeps the verdict it had; the server cleared it only if it ran and
        // failed. Nothing is raised here -- the user was editing a field, not
        // asking for this.
      } finally {
        useValidationStore.getState().completeValidation(pending.validationId);
      }
    }
  },
  setValidations: (validations) => {
    set({ validations: validations.map((validation) => new Validation(validation)) });
  },
  createValidationForParameter: async (parameterId: string) => {
    const response = await  new ValidationController().createValidation(parameterId);
    if (response.errors || response.status !== 200) return;
    const { setValidations, setParameters, validations, parameters } = get();
    setValidations([...validations, new Validation(response.validation)]);
    setParameters([...parameters, ...response.parameters]);
  },
  createContainerValidation: async (containerId: string) => {
    const response = await  new ValidationController().createContainerValidation(containerId);
    if (response.errors || response.status !== 200) return;
    const { setParameters, validations, parameters } = get();
    set({ validations: [...validations, new Validation(response.validation)] });
    setParameters([...parameters, ...response.parameters]);
  },
  updateValidation: async (request: ValidationUpdateRequest) => {
    const response = await new ValidationController().updateValidation(request);
    if (response.errors || response.status !== 200) return;
    const updatedValidation = new Validation(response.validation);
    const validations = get().validations.map((v) => (v.id === updatedValidation.id ? updatedValidation : v));
    set({ validations });
    // A changed script or rule makes the verdict it reached as stale as a
    // changed value would. This answers with the validation rather than with
    // parameters, so it never reaches `handleUpdatedParamsAndRefs`.
    void get().runPendingValidations(response.pendingValidations ?? []);
  },
  /** Runs one on demand, for a verdict somebody wants recomputed now. */
  rerunValidation: async (validationId: string) => {
    const response = await new ValidationController().prepareRun(validationId);
    if (response.errors || response.status !== 200) return;
    await get().runPendingValidations(response.pendingValidations ?? []);
  },
  deleteValidation: async (validationId: string) => {
    const response = await new ValidationController().deleteValidation(validationId);
    if (response.errors || response.status !== 200) return;
    const validations = get().validations.filter((v) => v.id !== validationId);
    set({ validations });
  },
  getValidationForSubject: (subjectId) => {
    if (!subjectId) return [];
    return get().validations.filter((v) => v.subjectId === subjectId);
  },
  validationsForParameters: (subjectIds: string[]) => {
    return get().validations.filter((v) => subjectIds.includes(v.subjectId));
  },
  parameterValidationsPassing: (parameterIds: string[]) => {
    if (parameterIds.length === 0) return true;
    const validationsOnParams = get().validationsForParameters(parameterIds);
    const paramsOnValidations = get().parametersForSubjectsAndTypes(validationsOnParams.map(v => v.id), [ParameterType.OUTPUT]);
    const contextLogs = get().contextLogs.filter(c => c.subjectType === CONTEXT_LOG_SUBJECT_TYPES.VALIDATION);

    const failingValidations = validationsOnParams.filter(v => {
      const matchingParamOnValidation = paramsOnValidations.find(p => p.subjectId === v.id);
      if (matchingParamOnValidation) {
        return matchingParamOnValidation.fields[0].customValue === false && !contextLogs.some(c => c.subjectId === v.id);
      }
      return false;
    });

    return failingValidations.length === 0;
  },
  fetchParametersForFunctionalInterface: async (functionalInterfaceId: string) => {
    const response = await new ParameterController().readParametersByFunctionalInterface(functionalInterfaceId);
    if (response.errors || response.status !== 200 || !response.parameters) return;
    get().setParameters(response.parameters);
  },
  addContextLog: async (request) => {
    const response = await new ContextLogController().create(request);
    if (response.status === 201) {
      set({ contextLogs: [...get().contextLogs, new ContextLog(response.contextLog)] });
    }
  },
  createParameterContextLog: async (parameterId: string) => {
    const response = await new ContextLogController().createParameterContextLog(parameterId);
    if (response.status === 201) {
      const { parameters, contextLogs } = get();
      const oldContextLog = contextLogs.find(c => c.subjectId === parameterId && c.status === CONTEXT_LOG_STATUS.PENDING);
      if (oldContextLog) {
        set({ contextLogs: contextLogs.filter(c => c.id !== oldContextLog.id) });
        const oldParameter = parameters.find(p => p.subjectId === oldContextLog.id);
        if (oldParameter) {
          set({ parameters: parameters.filter(p => p.id !== oldParameter.id) });
        }
      }
      set({ contextLogs: [...get().contextLogs, new ContextLog(response.contextLog)] });
      set({ parameters: [...get().parameters, new Parameter(response.parameter)] });
    }
  },
  commitParameterContextLog: async (contextLogId: string, note: string, userEmail: string) => {
    const response = await new ContextLogController().commitParameterContextLog(contextLogId, note, userEmail);
    const { setParameters, handleParameterResponse, parameters, contextLogs } = get();
    if (response.status === 200) {
      setParameters(parameters.filter((param) => param.subjectId !== contextLogId));
      handleParameterResponse(response.updatedParameters);
      set({ contextLogs: contextLogs.map((log) => (log.id === response.contextLog.id ? new ContextLog(response.contextLog) : log)) });
    }
  },
  fetchContextLogs: async (rootId: string) => {
    const response = await new ContextLogController().readContextLogs(rootId);
    if (response.errors || !response.contextLogs) return;
    set({ contextLogs: response.contextLogs.map((log) => new ContextLog(log)) });
  },
  deleteContextLogAndResetParameter: async (contextLog) => {
    void new ContextLogController().deleteContextLogAndResetParameter(contextLog.id).then(() => {
      set({ contextLogs: get().contextLogs.filter((log) => log.id !== contextLog.id) });
      useParameterStore.setState({ contextLogs: [...get().contextLogs] });
      void get().fetchParameter(contextLog.subjectId);
    });
  },
  deleteContextLogsForSubject: async (subjectId: string, subjectType: CONTEXT_LOG_SUBJECT_TYPES) => {
    void new ContextLogController().deleteContextLogForSubject(subjectId, subjectType).then(() => {
      set({ contextLogs: get().contextLogs.filter((log) => log.subjectId !== subjectId && log.subjectType !== subjectType) });
      useParameterStore.setState({ contextLogs: [...get().contextLogs] });
    });
  },
  deleteContextLog: async (contextLogId: string) => {
    const response = await new ContextLogController().deleteContextLog(contextLogId);
    if (response.status === 200) {
      set({ contextLogs: get().contextLogs.filter((log) => log.id !== contextLogId) });
    }
  },
  getValidationStatus: (validationId: string) => {
    if (useValidationStore.getState().isValidationRunning(validationId)) return ValidationStatus.RUNNING;
    const parameters = get().parametersForSubject(validationId, ParameterSubjectType.VALIDATION);
    const resultParam = parameters.find((p) => p.identifier === OPERAND_TYPE.RESULT);
    if (resultParam?.fields[0]?.customValue === true) return ValidationStatus.PASSING;
    const hasContextLog = get().contextLogs.some(c => c.subjectId === validationId && c.subjectType === CONTEXT_LOG_SUBJECT_TYPES.VALIDATION);
    if (hasContextLog) return ValidationStatus.OVERIDDEN;
    return ValidationStatus.FAILING;
  },
  getValidationStatusesForSubject: (subjectId) => {
    return Array.from(get().getValidationStatusMapForSubject(subjectId).values());
  },
  getValidationStatusMapForSubject: (subjectId) => {
    const validations = get().getValidationForSubject(subjectId);
    if (validations.length === 0) return new Map([[subjectId, ValidationStatus.NONE]]);
    return new Map(validations.map(v => [v.id, get().getValidationStatus(v.id)]));
  },
  fetchContextLogParametersForContextLogs: async (contextLogIds: string[]) => {
    const contextLogParametersResponse = await new ParameterController().readAllParametersBySubjects(contextLogIds, ParameterSubjectType.CONTEXT_LOG);
    if (contextLogParametersResponse.errors || contextLogParametersResponse.status !== 200) return;
    get().setParameters([...contextLogParametersResponse.parameters || []]);
  },
  handleValidationResponse: (validationParams: ValidationParams[]) => {
    if (validationParams.length === 0) return;
    set((state) => {
      const currentValidationIds = state.validations.map(validation => validation.id);
      const updatedValidationLookup: { [key: string]: ValidationParams } = {};
      const newValidations: Validation[] = [];

      validationParams.forEach(validation => {
        if (!currentValidationIds.includes(validation.id)) {
          newValidations.push(new Validation(validation));
        } else {
          updatedValidationLookup[validation.id] = validation;
        }
      });

      state.validations = [
        ...state.validations.map(validation =>
          updatedValidationLookup[validation.id] ? new Validation(updatedValidationLookup[validation.id]) : validation
        ),
        ...newValidations
      ];
    });
  },
  createProcedureTestParameters: async (procedureId: string) => {
    if (get().creatingTestParameters) return;
    set({ creatingTestParameters: true });
    const response = await new ParameterController().createProcedureTestParameters(procedureId);
    if (!response.errors && response.parameters) {
      const validParameters = get().parameters.filter((param) => !(param.subjectType === ParameterSubjectType.PROCEDURE && param.type === ParameterType.TEST_INPUT));
      get().setParameters(validParameters);
      get().handleParameterResponse(response.parameters);
    }
    set({ creatingTestParameters: false });
  },
})));
